import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StoreContext = createContext(null);

const GUEST_CART_KEY = "packo_cart";
const GUEST_WISHLIST_KEY = "packo_wishlist";

const readLS = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch {
    return fallback;
  }
};

const EMPTY_CART = { items: [], subtotal: 0, count: 0, issues: [] };

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(EMPTY_CART);
  const [cartLoading, setCartLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const mergedFor = useRef(null);

  const loadCart = useCallback(async () => {
    setCartLoading(true);
    try {
      if (user) {
        const { data } = await api.get("/cart");
        setCart(data);
      } else {
        const raw = readLS(GUEST_CART_KEY, []);
        if (!raw.length) {
          setCart(EMPTY_CART);
        } else {
          const { data } = await api.post("/shop/cart/resolve", { items: raw });
          setCart(data);
          localStorage.setItem(GUEST_CART_KEY, JSON.stringify(data.items.map((i) => ({ product_id: i.product_id, sku: i.sku, qty: i.qty }))));
        }
      }
    } catch {
      setCart(EMPTY_CART);
    } finally {
      setCartLoading(false);
    }
  }, [user]);

  const [gstEnabled, setGstEnabled] = useState(false);  // pre-GST mode default: OFF
  useEffect(() => {
    api.get("/shop/config").then(({ data }) => setGstEnabled(!!data.gst_enabled)).catch(() => {});
  }, []);

  // PAQVERO PRIME membership state (drives member prices + delivery benefit UI)
  const [prime, setPrime] = useState(null);
  const primeActive = prime?.state === "active";
  const loadPrime = useCallback(async () => {
    if (!user) { setPrime(null); return; }
    try {
      const { data } = await api.get("/prime/status");
      setPrime(data);
    } catch {
      setPrime(null);
    }
  }, [user]);
  useEffect(() => { loadPrime(); }, [loadPrime]);

  const loadWishlist = useCallback(async () => {
    setWishlistLoading(true);
    try {
      if (user) {
        const { data } = await api.get("/wishlist");
        setWishlistIds(data.product_ids);
        setWishlistProducts(data.products);
      } else {
        const ids = readLS(GUEST_WISHLIST_KEY, []);
        setWishlistIds(ids);
        if (ids.length) {
          const { data } = await api.get("/shop/products", { params: { ids: ids.join(","), limit: 48 } });
          setWishlistProducts(data.products); // list payload already carries default_sku (first purchasable variant)
        } else {
          setWishlistProducts([]);
        }
      }
    } catch {
      setWishlistProducts([]);
    } finally {
      setWishlistLoading(false);
    }
  }, [user]);

  const mergeGuestData = useCallback(async () => {
    const rawCart = readLS(GUEST_CART_KEY, []);
    const rawWishlist = readLS(GUEST_WISHLIST_KEY, []);
    try {
      if (rawCart.length) await api.post("/cart/merge", { items: rawCart });
      if (rawWishlist.length) await api.post("/wishlist/merge", { product_ids: rawWishlist });
    } catch { /* merge is best-effort; cart reload follows */ }
    localStorage.removeItem(GUEST_CART_KEY);
    localStorage.removeItem(GUEST_WISHLIST_KEY);
  }, []);

  useEffect(() => {
    if (user === undefined) return;
    const run = async () => {
      if (user && mergedFor.current !== user.id) {
        mergedFor.current = user.id;
        await mergeGuestData();
      }
      await Promise.all([loadCart(), loadWishlist()]);
    };
    run();
  }, [user, loadCart, loadWishlist, mergeGuestData]);

  const addItem = async (productId, sku, qty = 1, { silent = false, channel = "regular" } = {}) => {
    try {
      if (user) {
        const { data } = await api.post("/cart/items", { product_id: productId, sku, qty, channel });
        setCart(data);
      } else {
        const raw = readLS(GUEST_CART_KEY, []);
        const ex = raw.find((i) => i.product_id === productId && i.sku === sku && (i.channel || "regular") === channel);
        if (ex) ex.qty = Math.min(ex.qty + qty, 100000);
        else raw.push({ product_id: productId, sku, qty, channel });
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(raw));
        const { data } = await api.post("/shop/cart/resolve", { items: raw });
        setCart(data);
      }
      if (!silent) {
        toast.success("Added to cart");
        setDrawerOpen(true);
      }
      return true;
    } catch (e) {
      toast.error(apiError(e, "Couldn't add to cart"));
      return false;
    }
  };

  const setQty = async (productId, sku, qty, channel = "regular") => {
    if (qty < 1) return removeItem(productId, sku, channel);
    try {
      if (user) {
        const { data } = await api.put("/cart/items", { product_id: productId, sku, qty, channel });
        setCart(data);
      } else {
        const raw = readLS(GUEST_CART_KEY, []);
        const ex = raw.find((i) => i.product_id === productId && i.sku === sku && (i.channel || "regular") === channel);
        if (ex) ex.qty = qty;
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(raw));
        const { data } = await api.post("/shop/cart/resolve", { items: raw });
        setCart(data);
      }
    } catch (e) {
      toast.error(apiError(e, "Couldn't update quantity"));
    }
  };

  const removeItem = async (productId, sku, channel = "regular") => {
    if (user) {
      const { data } = await api.delete(`/cart/items/${productId}/${sku}`, { params: { channel } });
      setCart(data);
    } else {
      const raw = readLS(GUEST_CART_KEY, []).filter((i) => !(i.product_id === productId && i.sku === sku && (i.channel || "regular") === channel));
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(raw));
      if (!raw.length) setCart(EMPTY_CART);
      else {
        const { data } = await api.post("/shop/cart/resolve", { items: raw });
        setCart(data);
      }
    }
  };

  const clearGuestCart = () => {
    localStorage.removeItem(GUEST_CART_KEY);
    setCart(EMPTY_CART);
  };

  const toggleWishlist = async (productId) => {
    const wished = wishlistIds.includes(productId);
    try {
      if (user) {
        const { data } = wished
          ? await api.delete(`/wishlist/${productId}`)
          : await api.post(`/wishlist/${productId}`);
        setWishlistIds(data.product_ids);
        setWishlistProducts(data.products);
      } else {
        const ids = wished
          ? readLS(GUEST_WISHLIST_KEY, []).filter((i) => i !== productId)
          : [...readLS(GUEST_WISHLIST_KEY, []), productId];
        localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(ids));
        setWishlistIds(ids);
        setWishlistProducts((prev) => (wished ? prev.filter((p) => p.id !== productId) : prev));
        if (!wished) loadWishlist();
      }
      toast.success(wished ? "Removed from wishlist" : "Saved to wishlist");
    } catch (e) {
      toast.error(apiError(e, "Couldn't update wishlist"));
    }
  };

  const moveToCart = async (product) => {
    const sku = product.default_sku;
    if (!sku) {
      toast.error("Open the product to pick a variant");
      return;
    }
    const ok = await addItem(product.id, sku, product.min_order_qty || 1, { silent: true });
    if (ok) {
      await toggleWishlist(product.id);
      toast.success("Moved to cart");
      setDrawerOpen(true);
    }
  };

  const saveForLater = async (item) => {
    if (!wishlistIds.includes(item.product_id)) await toggleWishlist(item.product_id);
    await removeItem(item.product_id, item.sku);
    toast.success("Saved for later");
  };

  return (
    <StoreContext.Provider
      value={{
        cart,
        cartCount: cart.count || 0,
        cartLoading,
        gstEnabled,
        drawerOpen,
        setDrawerOpen,
        addItem,
        setQty,
        removeItem,
        clearGuestCart,
        refreshCart: loadCart,
        wishlistIds,
        wishlistProducts,
        wishlistLoading,
        toggleWishlist,
        moveToCart,
        saveForLater,
        prime,
        primeActive,
        refreshPrime: loadPrime,
        user,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
